import { Button, DialogClose, DialogContent, DialogFooter, DialogHeader, DialogRoot, DialogTitle } from '@ibsys/kit';
import { FC, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ThemeValue, UpdateThemeRequest } from '@/entities/themes';
import { useUpdateTheme } from '@/entities/themes';
import { DESTRUCTIVE_BUTTON_CLASS } from '@/shared/lib';
import { Dropzone } from '@/shared/ui/Dropzone';

type UploadLogoFaviconDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  theme: ThemeValue;
};

// Конвертирует выбранный файл в data URL
const fileToDataUrl = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

export const UploadLogoFaviconDialog: FC<UploadLogoFaviconDialogProps> = ({ isOpen, onClose, theme }) => {
  const { t } = useTranslation(['common', 'themes']);
  const { mutateAsync: updateTheme, isPending } = useUpdateTheme();

  const [logoSvg, setLogoSvg] = useState('');
  const [favicon, setFavicon] = useState('');

  // Сбрасываем локальное состояние при каждом открытии диалога
  useEffect(() => {
    if (isOpen) {
      setLogoSvg('');
      setFavicon('');
    }
  }, [isOpen]);

  const handleLogoFiles = useCallback(async (files: File[]) => {
    if (files.length > 0) {
      const dataUrl = await fileToDataUrl(files[0]);
      setLogoSvg(dataUrl);
    }
  }, []);

  const handleFaviconFiles = useCallback(async (files: File[]) => {
    if (files.length > 0) {
      const dataUrl = await fileToDataUrl(files[0]);
      setFavicon(dataUrl);
    }
  }, []);

  const handleClose = useCallback(() => {
    setLogoSvg('');
    setFavicon('');
    onClose();
  }, [onClose]);

  const handleSave = useCallback(async () => {
    // Если ни логотип, ни favicon не были загружены — просто закрываем окно
    if (!logoSvg && !favicon) {
      handleClose();
      return;
    }

    // Формируем тело запроса на основе текущей темы.
    // Поля logoSvg и favicon переопределяем только если пользователь загрузил новые файлы.
    const body: UpdateThemeRequest = {
      code: theme.code,
      name: theme.name,
      isActive: theme.isActive,
      logoSvg: logoSvg || theme.logoSvg,
      colorParameterValues: theme.colorParameterValues.map((value) => ({
        parameterCode: value.parameterCode,
        colorHex: value.colorHex,
        colorHslH: value.colorHslH,
        colorHslS: value.colorHslS,
        colorHslL: value.colorHslL,
        colorHslA: value.colorHslA,
      })),
    };

    if (favicon) {
      body.favicon = favicon;
    }

    await updateTheme({ id: theme.id, body });
    handleClose();
  }, [logoSvg, favicon, theme, updateTheme, handleClose]);

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) {
        handleClose();
      }
    },
    [handleClose],
  );

  const hasUploads = Boolean(logoSvg) || Boolean(favicon);

  return (
    <DialogRoot open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('themes:uploadDialog.title')}</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">{t('themes:createDialog.logoLabel')}</span>
            {logoSvg ? (
              <div className="flex items-center gap-2">
                <img src={logoSvg} alt="logo" className="h-10 max-w-32 object-contain" />
                <Button
                  variant="ghost"
                  color="destructive"
                  className={DESTRUCTIVE_BUTTON_CLASS}
                  size="sm"
                  onClick={() => setLogoSvg('')}
                >
                  {t('common:delete')}
                </Button>
              </div>
            ) : (
              <Dropzone
                acceptedFileTypes={['.svg', '.png', '.jpg', '.jpeg', '.webp']}
                maxFiles={1}
                maxFileSize={5}
                onFilesChange={handleLogoFiles}
              />
            )}
          </div>

          <div className="flex flex-col gap-2">
            <span className="text-sm font-medium">{t('themes:createDialog.faviconLabel')}</span>
            {favicon ? (
              <div className="flex items-center gap-2">
                <img src={favicon} alt="favicon" className="size-8 object-contain" />
                <Button
                  variant="ghost"
                  color="destructive"
                  className={DESTRUCTIVE_BUTTON_CLASS}
                  size="sm"
                  onClick={() => setFavicon('')}
                >
                  {t('common:delete')}
                </Button>
              </div>
            ) : (
              <Dropzone
                acceptedFileTypes={['.ico', '.png', '.svg']}
                maxFiles={1}
                maxFileSize={1}
                onFilesChange={handleFaviconFiles}
              />
            )}
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <DialogClose>
            <Button variant="ghost">{t('common:cancel')}</Button>
          </DialogClose>
          <Button onClick={handleSave} isLoading={isPending} disabled={!hasUploads}>
            {t('common:save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
};
